import React, { useEffect, useState } from 'react';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Chart } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, Col } from 'reactstrap';
import SmallLoader from '../../../../shared/components/SmallLoader';
import { setCurrencyFormat } from '../../../../utils/helpers';

// Remove this line - it's causing the error
// Chart.plugins.register(ChartDataLabels);

const colorArr = [
  '#5276ff',
  '#ffbca0',
  '#ff6d8c',
  '#b4ffa0',
  '#ffeba0',
  '#ffa0b4',
  '#a0ffeb',
  '#5c85ff',
  '#34ebb4',
  '#eb8034',
  '#34a8eb',
  '#bca0ff',
];

const colorData = (num, hover = false) => {
  let color = colorArr[num - 1];
  if (hover) {
    color = `${colorArr[num - 1]}`;
  }
  return color;
};

const addMonthlyAmount = (total, num) => (Number(total) + Number(num)).toFixed(2);

const reduceArray = (obj) => {
  if (obj && obj.length > 0) {
    return obj.reduce(addMonthlyAmount);
  }
  return 0;
};

const getYearsList = (dataSet) => {
  const yearArray = [];
  if (dataSet && dataSet.length > 0) {
    dataSet.forEach((innerObj) => {
      if (!yearArray.includes(innerObj.salesYear)) {
        yearArray.push(innerObj.salesYear);
      }
    });
  }
  return yearArray;
};

const getDataSets = (dataSet) => {
  if (dataSet && dataSet.length > 0) {
    const yearArray = getYearsList(dataSet);

    const monthSetData = {
      1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [], 8: [],
      9: [], 10: [], 11: [], 12: []
    }

    const yearWiseData = dataSet?.reduce((acc, current) => {
      if (acc[current?.salesYear]) {
        acc[current?.salesYear].push(current)
      } else {
        acc[current?.salesYear] = [current];
      }
      return acc
    }, {});

    yearArray.forEach((ele) => {
      const yearData = yearWiseData[ele];

      const monthWiseData = yearData?.reduce((acc, current) => {
        if (acc[current?.salesMonth]) {
          acc[current?.salesMonth].push(current)
        } else {
          acc[current?.salesMonth] = [current];
        }
        return acc
      }, {});

      monthSetData[1].push(monthWiseData[1]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[2].push(monthWiseData[2]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[3].push(monthWiseData[3]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[4].push(monthWiseData[4]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[5].push(monthWiseData[5]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[6].push(monthWiseData[6]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[7].push(monthWiseData[7]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[8].push(monthWiseData[8]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[9].push(monthWiseData[9]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[10].push(monthWiseData[10]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[11].push(monthWiseData[11]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));
      monthSetData[12].push(monthWiseData[12]?.reduce((acc, item) => Number(item.amount) + acc, 0).toFixed(2));

    });

    const year = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return year.map((month, index) => ({
      key: index + 1,
      label: month,
      borderColor: colorData(index + 1),
      borderWidth: 0,
      hoverBackgroundColor: colorData(index + 1, true),
      hoverBorderColor: colorData(index + 1, true),
      data: monthSetData[index + 1],
      backgroundColor: colorData(index + 1),
      datalabels: {
        align: 'end',
        anchor: 'end',
        rotation: 270,
        font: {
          weight: 500,
          size: 10,
        },
        formatter: value => `${setCurrencyFormat(value, true)}`,
      },
    }))
  }
  return [];
};

const getState = data => ({
  labels: getYearsList(data),
  datasets: getDataSets(data),
});

const options = {
  // Register the plugin here instead of globally
  plugins: [ChartDataLabels],
  tooltips: {
    callbacks: {
      label: (tooltipItem, data) => {
        let label = data.datasets[tooltipItem.datasetIndex].label || '';

        if (label) {
          label += ': ';
        }

        // eslint-disable-next-line max-len
        label += setCurrencyFormat(tooltipItem.yLabel.toFixed(2), true); // Math.round(tooltipItem.yLabel * 100) / 100;
        return label;
      },
    },
  },
  datalabels: {
    display: true,
    color: '#000',
  },
  legend: {
    display: false,
  },

  // Core options
  layout: {
    padding: {
      top: 24,
      right: 0,
      bottom: 0,
      left: 8,
    },
  },
  elements: {
    line: {
      fill: false,
    },
    point: {
      hoverRadius: 7,
      radius: 5,
    },
  },
  scales: {
    xAxes: [
      {
        stacked: false,
      },
    ],
    yAxes: [
      {
        stacked: false,
      },
    ],
  },
};

const MonthlySales = (props) => {
  const { title, revenueData, setModel, isLoading } = props;
  const { t } = useTranslation('common');
  const [data, setData] = useState();
  const [currentYearTotalAmount, setCurrentYearTotalAmount] = useState(0);
  const [previousYearTotalAmount, setPreviousYearTotalAmount] = useState(0);
 
  useEffect(() => {
    setData(revenueData ? getState(revenueData) : []);
    if (revenueData && revenueData.length > 0) {
      setCurrentYearTotalAmount(
        revenueData
          .filter(
            item =>
              Number(item.salesYear) === new Date().getFullYear() &&
              Number(item.salesMonth) === new Date().getMonth() + 1,
          )
          .reduce((acc, current) => acc + Number(current?.amount), 0)
          .toFixed(2),
      );
      setPreviousYearTotalAmount(
        revenueData
          .filter(
            item =>
              Number(item.salesYear) === new Date().getFullYear() - 1 &&
              Number(item.salesMonth) === new Date().getMonth(),
          )
          .reduce((acc, current) => acc + Number(current?.amount), 0)
          .toFixed(2),
      );
    }
  }, [revenueData]);

  const totalAmount = reduceArray(revenueData?.map(x => x.amount));
  const totalAmounts = (totalAmount / 1000000).toFixed(2);

  const getPercentage = (currValue, preValue) =>
    (((currValue - preValue) / preValue) * 100).toFixed(2) || '0.00';

  return (
    <Col md={12} lg={12} xl={12}>
      <Card>
        <CardBody className="card-shadow">
          {isLoading ? (<>
            <div className="card__title">
              <h5 className="bold-text">{t(title)}</h5>
            </div>
            <div
              style={{ minHeight: '200px' }}
              className="row align-items-center justify-content-center"
            >
              <SmallLoader loading={isLoading} />
            </div>
          </>
          ) : (<>
            <div className="card__title">
              <h5 className="bold-text">{t(title)}</h5>
              <button id='btnMonthlyViewReport'
                type="button"
                className="view-report btn btn-primary btn-sm"
                onClick={() => setModel(true)}
              >
                View Report
              </button>
            </div>
            <div className="second-header-for-chart">
              <div className="left">
                <h5 className="bold-text colored">$ {totalAmounts} Million</h5>
                <h5 className="bold-text">{t('Total Amount')}</h5>
              </div>
              <div className="right">
                {getPercentage(currentYearTotalAmount, previousYearTotalAmount) < 0 && (
                  <h5 className="bold-text colored">
                    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M16,18L18.29,15.71L13.41,10.83L9.41,14.83L2,7.41L3.41,6L9.41,12L13.41,8L19.71,
                        14.29L22,12V18H16Z"
                      />
                    </svg>
                    {getPercentage(currentYearTotalAmount, previousYearTotalAmount)}%
                  </h5>
                )}
                {getPercentage(currentYearTotalAmount, previousYearTotalAmount) > 0 && (
                  <h5 className="bold-text colored">
                    <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,
                        12L13.41,16L19.71,9.71L22,12V6H16Z"
                      />
                    </svg>
                    {getPercentage(currentYearTotalAmount, previousYearTotalAmount)}%
                  </h5>
                )}
                <h5 className="bold-text">{t('Since last month')}</h5>
              </div>
            </div>
            <div style={{ marginTop: '120px' }}>
              <Bar data={data} options={options} />
            </div>
          </>)}
        </CardBody>
      </Card>
    </Col>
  );
};
MonthlySales.propTypes = {
  title: PropTypes.string.isRequired,
  revenueData: PropTypes.string.isRequired,
  setModel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired
};

export default MonthlySales;